'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { formatPrice, CurrencyCode } from '@/lib/travelpayouts';

interface FlightResult {
  id: string;
  airline: string;
  airlineCode: string;
  origin: string;
  originCode: string;
  destination: string;
  destinationCode: string;
  departTime: string;
  arriveTime: string;
  duration: string;
  stops: string;
  stopDetail?: string;
  price: number;
  originalPrice?: number;
  badge?: string;
  badgeType?: 'best' | 'cheapest' | 'popular';
  priceTrend: 'up' | 'down' | 'stable';
  priceTrendAmount?: number;
  seatsLeft?: number;
  partner: string;
  returnDepart?: string;
  returnArrive?: string;
}

const flightResults: FlightResult[] = [
  {
    id: 'flight-001',
    airline: 'British Airways',
    airlineCode: 'BA',
    origin: 'New York',
    originCode: 'JFK',
    destination: 'London',
    destinationCode: 'LHR',
    departTime: '08:15',
    arriveTime: '20:30',
    duration: '7h 15m',
    stops: 'Nonstop',
    price: 389,
    originalPrice: 612,
    badge: 'Best Value',
    badgeType: 'best',
    priceTrend: 'down',
    priceTrendAmount: 43,
    partner: 'Expedia',
    returnDepart: '21 Jun 14:00',
    returnArrive: '21 Jun 17:10',
  },
  {
    id: 'flight-002',
    airline: 'American Airlines',
    airlineCode: 'AA',
    origin: 'New York',
    originCode: 'JFK',
    destination: 'London',
    destinationCode: 'LHR',
    departTime: '10:40',
    arriveTime: '23:55',
    duration: '8h 15m',
    stops: 'Nonstop',
    price: 412,
    priceTrend: 'stable',
    partner: 'American Airlines',
    returnDepart: '21 Jun 18:30',
    returnArrive: '21 Jun 21:45',
  },
  {
    id: 'flight-003',
    airline: 'Delta',
    airlineCode: 'DL',
    origin: 'New York',
    originCode: 'JFK',
    destination: 'London',
    destinationCode: 'LGW',
    departTime: '17:20',
    arriveTime: '07:35+1',
    duration: '9h 15m',
    stops: 'Nonstop',
    price: 374,
    badge: 'Cheapest',
    badgeType: 'cheapest',
    priceTrend: 'down',
    priceTrendAmount: 28,
    seatsLeft: 3,
    partner: 'Kayak',
    returnDepart: '21 Jun 09:00',
    returnArrive: '21 Jun 12:15',
  },
  {
    id: 'flight-004',
    airline: 'Lufthansa',
    airlineCode: 'LH',
    origin: 'New York',
    originCode: 'JFK',
    destination: 'London',
    destinationCode: 'LHR',
    departTime: '20:05',
    arriveTime: '11:30+1',
    duration: '11h 25m',
    stops: '1 stop',
    stopDetail: 'via Frankfurt (FRA) · 1h 55m layover',
    price: 298,
    originalPrice: 445,
    priceTrend: 'down',
    priceTrendAmount: 67,
    partner: 'Booking.com',
    returnDepart: '21 Jun 20:10',
    returnArrive: '22 Jun 07:45',
  },
  {
    id: 'flight-005',
    airline: 'Air France',
    airlineCode: 'AF',
    origin: 'New York',
    originCode: 'JFK',
    destination: 'London',
    destinationCode: 'LHR',
    departTime: '22:30',
    arriveTime: '14:55+1',
    duration: '12h 25m',
    stops: '1 stop',
    stopDetail: 'via Paris (CDG) · 2h 10m layover',
    price: 312,
    priceTrend: 'up',
    priceTrendAmount: 18,
    partner: 'Air France',
    returnDepart: '21 Jun 16:45',
    returnArrive: '22 Jun 04:20',
  },
  {
    id: 'flight-006',
    airline: 'United',
    airlineCode: 'UA',
    origin: 'New York',
    originCode: 'EWR',
    destination: 'London',
    destinationCode: 'LHR',
    departTime: '07:00',
    arriveTime: '19:10',
    duration: '7h 10m',
    stops: 'Nonstop',
    price: 448,
    priceTrend: 'stable',
    badge: 'Popular',
    badgeType: 'popular',
    seatsLeft: 7,
    partner: 'United Airlines',
    returnDepart: '21 Jun 21:00',
    returnArrive: '22 Jun 00:10',
  },
  {
    id: 'flight-007',
    airline: 'Emirates',
    airlineCode: 'EK',
    origin: 'New York',
    originCode: 'JFK',
    destination: 'London',
    destinationCode: 'LHR',
    departTime: '23:55',
    arriveTime: '18:40+1',
    duration: '13h 45m',
    stops: '1 stop',
    stopDetail: 'via Dubai (DXB) · 2h 30m layover',
    price: 276,
    originalPrice: 520,
    priceTrend: 'down',
    priceTrendAmount: 94,
    partner: 'Priceline',
    returnDepart: '21 Jun 23:55',
    returnArrive: '23 Jun 06:20',
  },
  {
    id: 'flight-008',
    airline: 'Iberia',
    airlineCode: 'IB',
    origin: 'New York',
    originCode: 'JFK',
    destination: 'London',
    destinationCode: 'LHR',
    departTime: '18:45',
    arriveTime: '10:05+1',
    duration: '12h 20m',
    stops: '1 stop',
    stopDetail: 'via Madrid (MAD) · 1h 45m layover',
    price: 334,
    priceTrend: 'stable',
    partner: 'Trip.com',
    returnDepart: '21 Jun 12:30',
    returnArrive: '22 Jun 01:15',
  },
];

function AirlineAvatar({ code, name }: { code: string; name: string }) {
  const colorMap: Record<string, string> = {
    BA: 'bg-blue-600',
    AA: 'bg-red-600',
    DL: 'bg-red-700',
    LH: 'bg-yellow-500',
    AF: 'bg-blue-800',
    UA: 'bg-blue-900',
    EK: 'bg-red-500',
    IB: 'bg-red-800',
  };
  return (
    <div
      className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${colorMap[code] || 'bg-muted'}`}
      title={name}
    >
      <span className="text-white text-xs font-800">{code}</span>
    </div>
  );
}

export default function FlightResultsList({ sortBy, currency = 'USD' }: { sortBy: string; currency?: CurrencyCode }) {
  const [wishlisted, setWishlisted] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [redirecting, setRedirecting] = useState<string | null>(null);

  const toggleWishlist = (id: string) => {
    setWishlisted((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleViewDeal = (id: string) => {
    // BACKEND: Track affiliate click event, then redirect to partner URL
    setRedirecting(id);
    setTimeout(() => setRedirecting(null), 1500);
  };

  const sorted = [...flightResults].sort((a, b) => {
    if (sortBy === 'price-asc') return a.price - b.price;
    if (sortBy === 'price-desc') return b.price - a.price;
    if (sortBy === 'duration-asc') return a.duration.localeCompare(b.duration);
    return 0;
  });

  return (
    <div className="space-y-3 fade-in">
      {sorted.map((flight) => (
        <div
          key={flight.id}
          className="bg-card border border-border rounded-2xl overflow-hidden result-card-hover"
        >
          <div className="p-4">
            <div className="flex flex-wrap items-start gap-4">
              {/* Airline */}
              <div className="flex items-center gap-3 flex-shrink-0">
                <AirlineAvatar code={flight.airlineCode} name={flight.airline} />
                <div>
                  <p className="text-sm font-700 text-foreground">{flight.airline}</p>
                  <p className="text-xs text-muted-foreground">{flight.airlineCode} · Economy</p>
                </div>
              </div>

              {/* Route */}
              <div className="flex-1 min-w-0">
                {/* Outbound */}
                <div className="flex items-center gap-3">
                  <div className="text-center">
                    <p className="text-lg font-800 text-foreground tabular-nums">{flight.departTime}</p>
                    <p className="text-xs text-muted-foreground font-semibold">{flight.originCode}</p>
                  </div>
                  <div className="flex-1 flex flex-col items-center gap-0.5 min-w-[80px]">
                    <p className="text-xs text-muted-foreground tabular-nums">{flight.duration}</p>
                    <div className="relative w-full flex items-center">
                      <div className="h-px flex-1 bg-border" />
                      <Icon name="PaperAirplaneIcon" size={12} className="text-primary mx-1" />
                      <div className="h-px flex-1 bg-border" />
                    </div>
                    <p
                      className={`text-xs font-semibold ${
                        flight.stops === 'Nonstop' ? 'text-positive' : 'text-muted-foreground'
                      }`}
                    >
                      {flight.stops}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-800 text-foreground tabular-nums">{flight.arriveTime}</p>
                    <p className="text-xs text-muted-foreground font-semibold">{flight.destinationCode}</p>
                  </div>
                </div>

                {/* Return hint */}
                {flight.returnDepart && (
                  <p className="text-xs text-muted-foreground mt-1.5">
                    ↩ Return: {flight.returnDepart} → {flight.returnArrive}
                  </p>
                )}

                {/* Stop detail */}
                {flight.stopDetail && expanded.has(flight.id) && (
                  <div className="mt-2 px-3 py-2 bg-secondary rounded-lg text-xs text-muted-foreground">
                    <Icon name="InformationCircleIcon" size={12} className="inline mr-1 text-primary" />
                    {flight.stopDetail}
                  </div>
                )}
              </div>

              {/* Price + CTA */}
              <div className="flex flex-col items-end gap-2 flex-shrink-0 min-w-[140px]">
                {/* Badge */}
                {flight.badge && (
                  <span
                    className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                      flight.badgeType === 'best' ?'price-badge-best'
                        : flight.badgeType === 'cheapest' ?'price-badge-cheapest' :'price-badge-popular'
                    }`}
                  >
                    {flight.badge}
                  </span>
                )}

                {/* Price */}
                <div className="text-right">
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-2xl font-800 text-foreground tabular-nums">
                      {formatPrice(flight.price, currency)}
                    </span>
                    <span className="text-xs text-muted-foreground">/ person</span>
                  </div>
                  {flight.originalPrice && (
                    <p className="text-xs text-muted-foreground line-through tabular-nums">
                      {formatPrice(flight.originalPrice, currency)}
                    </p>
                  )}
                  {flight.priceTrend !== 'stable' && (
                    <p
                      className={`text-xs font-semibold flex items-center gap-0.5 justify-end ${
                        flight.priceTrend === 'down' ? 'price-trend-down' : 'price-trend-up'
                      }`}
                    >
                      <Icon
                        name={flight.priceTrend === 'down' ? 'ArrowTrendingDownIcon' : 'ArrowTrendingUpIcon'}
                        size={12}
                      />
                      {flight.priceTrend === 'down' ? '-' : '+'}
                      {formatPrice(flight.priceTrendAmount || 0, currency)} vs avg
                    </p>
                  )}
                </div>

                {/* Seats left */}
                {flight.seatsLeft && (
                  <p className="text-xs text-warning font-semibold">
                    Only {flight.seatsLeft} seats left!
                  </p>
                )}

                {/* CTA */}
                <button
                  type="button"
                  onClick={() => handleViewDeal(flight.id)}
                  className="btn-primary px-5 py-2.5 text-sm flex items-center gap-2 min-w-[120px] justify-center"
                  disabled={redirecting === flight.id}
                >
                  {redirecting === flight.id ? (
                    <>
                      <Icon name="ArrowPathIcon" size={14} className="animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      View Deal
                      <Icon name="ArrowTopRightOnSquareIcon" size={14} />
                    </>
                  )}
                </button>

                <p className="text-xs text-muted-foreground">via {flight.partner}</p>
              </div>
            </div>
          </div>

          {/* Expand footer */}
          <div className="border-t border-border px-4 py-2 flex items-center justify-between bg-background/40">
            <button
              type="button"
              onClick={() => toggleExpand(flight.id)}
              className="text-xs text-primary font-semibold hover:underline flex items-center gap-1"
            >
              <Icon name={expanded.has(flight.id) ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={12} />
              {expanded.has(flight.id) ? 'Hide details' : 'Flight details'}
            </button>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => toggleWishlist(flight.id)}
                aria-label={wishlisted.has(flight.id) ? 'Remove from wishlist' : 'Save to wishlist'}
                className={`wishlist-btn flex items-center gap-1 text-xs font-medium transition-colors ${
                  wishlisted.has(flight.id) ? 'text-negative' : 'text-muted-foreground hover:text-negative'
                }`}
              >
                <Icon
                  name="HeartIcon"
                  variant={wishlisted.has(flight.id) ? 'solid' : 'outline'}
                  size={14}
                />
                {wishlisted.has(flight.id) ? 'Saved' : 'Save'}
              </button>
              <span className="text-muted-foreground text-xs">·</span>
              <button
                type="button"
                className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
              >
                <Icon name="ShareIcon" size={14} />
                Share
              </button>
            </div>
          </div>
        </div>
      ))}

      {/* Load more */}
      <div className="text-center pt-4">
        <button
          type="button"
          className="btn-secondary px-8 py-3 text-sm"
        >
          Show 12 more results
        </button>
        <p className="text-xs text-muted-foreground mt-2">
          Prices shown are indicative. Final price confirmed at partner site.
        </p>
      </div>
    </div>
  );
}